import React, { useCallback, useEffect, useState } from 'react';
import { shelf as c } from '../../component-classes';
import { Carousel, CarouselItem } from '../carousel/Carousel';
import PagingButton from './PagingButton';
import { Direction, usePaging } from './usePaging';
import '@fabric-ds/icons/elements/chevron-left-16';
import '@fabric-ds/icons/elements/chevron-right-16';

const GRID_GAP = 16;
const MAGIC_NUMBER = 32;

type CollectionsShelfProps = React.PropsWithChildren<{
    className?: string;
    heading: string;
    snap?: boolean;
}>;

function CollectionsShelf(props: CollectionsShelfProps) {
    const { children, className, heading, snap = false } = props;

    const [pageTotal, setPageTotal] = useState(0);
    const [shelfElement, setShelfElement] = useState<HTMLDivElement>();

    const { carouselRef, currentPage, handlePagingClick, handleScroll } =
        usePaging(pageTotal, GRID_GAP);

    const shelfRef = useCallback((callbackElement: HTMLDivElement) => {
        if (callbackElement !== null) {
            setShelfElement(callbackElement);
        }
    }, []);

    useEffect(() => {
        if (shelfElement && carouselRef.current) {
            const pageTotal = Math.ceil(
                (carouselRef.current.scrollState.scrollWidth - MAGIC_NUMBER) /
                    shelfElement.offsetWidth,
            );
            setPageTotal(pageTotal);
        }
    }, [shelfElement, carouselRef]);

    return (
        <section className={className}>
            <div className={c.header}>
                <h2 className={c.title}>{heading}</h2>
                {pageTotal > 1 && (
                    <div className={c.pagingToolbar}>
                        <span className={c.pagingLabel}>
                            {currentPage + 1} av {pageTotal}
                        </span>
                        <PagingButton
                            // @ts-expect-error
                            icon={<f-icon-chevron-left16 />}
                            label="Forrige"
                            onClick={() => handlePagingClick(Direction.LEFT)}
                            style={{ marginRight: '8px' }}
                        />
                        <PagingButton
                            // @ts-expect-error
                            icon={<f-icon-chevron-right16 />}
                            label="Neste"
                            onClick={() => handlePagingClick(Direction.RIGHT)}
                        />
                    </div>
                )}
            </div>
            <div ref={shelfRef} className={c.fade}>
                <Carousel
                    ref={carouselRef}
                    className="flex space-x-8 lg:space-x-16"
                    bleed
                    onScroll={handleScroll}
                    smooth
                    snap={snap}
                >
                    {React.Children.map(children, (child) => (
                        <CarouselItem>{child}</CarouselItem>
                    ))}
                </Carousel>
            </div>
        </section>
    );
}

export default CollectionsShelf;
